import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { UserName } from "@/components/badges/UserName";
import { Trophy, Medal, Award, Star } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { getStorageUrl } from "@/lib/storage";

interface ActiveUser {
  id: string;
  full_name: string;
  avatar_url: string | null;
  role: string;
  activityScore: number;
  modulesCompleted: number;
  assignmentsSubmitted: number;
  quizzesPassed: number;
  discussionsCreated: number;
  repliesPosted: number;
}

export function DashboardLeaderboard() {
  const { data: leaderboard, isLoading } = useQuery({
    queryKey: ["dashboard_leaderboard"],
    queryFn: async () => {
      // Fetch all profiles
      const { data: profiles, error: profilesError } = await supabase
        .from("profiles")
        .select("id, full_name, avatar_url, role");

      if (profilesError) throw profilesError;
      if (!profiles || profiles.length === 0) return [];

      const userIds = profiles.map((p) => p.id);

      // Fetch module progress (completed modules)
      const { data: moduleProgress } = await supabase
        .from("module_progress")
        .select("enrollment_id, module_id, completed_at")
        .not("completed_at", "is", null);

      // Fetch course enrollments to map enrollment_id to user_id
      const { data: courseEnrollments } = await supabase
        .from("course_enrollments")
        .select("id, user_id")
        .in("user_id", userIds);

      const enrollmentToUser = new Map<string, string>();
      courseEnrollments?.forEach((e) => enrollmentToUser.set(e.id, e.user_id));

      // Fetch assignment submissions
      const { data: assignmentSubs } = await supabase
        .from("assignment_submissions")
        .select("user_id")
        .in("user_id", userIds);

      // Fetch quiz attempts (passed)
      const { data: quizAttempts } = await supabase
        .from("quiz_attempts")
        .select("user_id, passed")
        .in("user_id", userIds)
        .eq("passed", true);

      // Fetch general discussions
      const { data: generalDiscussions } = await supabase
        .from("general_discussions")
        .select("user_id")
        .in("user_id", userIds);

      // Fetch general discussion replies
      const { data: generalReplies } = await supabase
        .from("general_discussion_replies")
        .select("user_id")
        .in("user_id", userIds);

      // Fetch course discussions
      const { data: courseDiscussions } = await supabase
        .from("course_discussions")
        .select("user_id")
        .in("user_id", userIds);

      // Fetch discussion replies
      const { data: discussionReplies } = await supabase
        .from("discussion_replies")
        .select("user_id")
        .in("user_id", userIds);

      // Calculate scores per user
      const userScores: ActiveUser[] = profiles.map((profile) => {
        const userId = profile.id;

        // Modules completed
        const modulesCompleted =
          moduleProgress?.filter(
            (mp) => enrollmentToUser.get(mp.enrollment_id) === userId
          ).length || 0;

        // Assignments submitted
        const assignmentsSubmitted =
          assignmentSubs?.filter((a) => a.user_id === userId).length || 0;

        // Quizzes passed
        const quizzesPassed =
          quizAttempts?.filter((q) => q.user_id === userId).length || 0;

        // Discussions created (general + course)
        const discussionsCreated =
          (generalDiscussions?.filter((d) => d.user_id === userId).length || 0) +
          (courseDiscussions?.filter((d) => d.user_id === userId).length || 0);

        // Replies posted (general + course)
        const repliesPosted =
          (generalReplies?.filter((r) => r.user_id === userId).length || 0) +
          (discussionReplies?.filter((r) => r.user_id === userId).length || 0);

        // Weighted activity score
        const activityScore =
          modulesCompleted * 5 +
          assignmentsSubmitted * 4 +
          quizzesPassed * 3 +
          discussionsCreated * 2 +
          repliesPosted * 1;

        return {
          id: userId,
          full_name: profile.full_name,
          avatar_url: profile.avatar_url,
          role: profile.role,
          activityScore,
          modulesCompleted,
          assignmentsSubmitted,
          quizzesPassed,
          discussionsCreated,
          repliesPosted,
        };
      });

      // Sort by activity score and return top 10
      return userScores
        .filter((u) => u.activityScore > 0)
        .sort((a, b) => b.activityScore - a.activityScore)
        .slice(0, 10);
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  const getRankIcon = (rank: number) => {
    switch (rank) {
      case 1:
        return <Trophy className="h-5 w-5 text-yellow-500" />;
      case 2:
        return <Medal className="h-5 w-5 text-gray-400" />;
      case 3:
        return <Award className="h-5 w-5 text-amber-600" />;
      default:
        return (
          <span className="w-5 h-5 flex items-center justify-center text-sm font-medium text-muted-foreground">
            {rank}
          </span>
        );
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Star className="h-5 w-5 text-accent" />
            Most Active Learners
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex items-center gap-3">
              <Skeleton className="h-5 w-5 rounded" />
              <Skeleton className="h-8 w-8 rounded-full" />
              <div className="flex-1 space-y-1">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-3 w-16" />
              </div>
              <Skeleton className="h-4 w-12" />
            </div>
          ))}
        </CardContent>
      </Card>
    );
  }

  if (!leaderboard || leaderboard.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Star className="h-5 w-5 text-accent" />
          Most Active Learners
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {leaderboard.map((user, index) => (
            <div
              key={user.id}
              className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors"
            >
              <div className="flex-shrink-0">{getRankIcon(index + 1)}</div>
              <Avatar className="h-8 w-8">
                <AvatarImage src={getStorageUrl("avatars", user.avatar_url) || undefined} />
                <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                  {user.full_name?.charAt(0) || "?"}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate"><UserName userId={user.id} name={user.full_name} /></p>
                <Badge variant="outline" className="text-xs capitalize">
                  {user.role}
                </Badge>
              </div>
              <div className="text-right">
                <p className="text-sm font-semibold text-accent">{user.activityScore}</p>
                <p className="text-xs text-muted-foreground">pts</p>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
